import { create } from "zustand"
import type { ViewId } from "@/features/views/viewRegistry"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TagFilterState {
  /** Tags currently selected by the user for filtering (empty = "All" = no filter) */
  selectedTags: string[]
  /** Tags available on tasks in the current view */
  availableTags: string[]
  /** Free-text filter for task titles */
  filterText: string

  // ── Actions ──
  setSelectedTags: (tags: string[]) => void
  setAvailableTags: (tags: string[]) => void
  setFilterText: (text: string) => void
  /** Toggle a single tag in/out of the selection */
  toggleTag: (tag: string) => void
  /** Select "All" — clears selection */
  selectAll: () => void
  /** Reset all filter state (on navigation) */
  reset: () => void
  /** Whether any filter is active */
  isFiltering: () => boolean
}

// ---------------------------------------------------------------------------
// Views that display the tag filter bar
// ---------------------------------------------------------------------------

const TAG_FILTER_VIEWS: Set<ViewId> = new Set([
  "today",
  "evening",
  "anyday",
  "upcoming",
  "someday",
  "logbook",
  "trash",
  "project",
  "list",
  "area",
])

/** Check if a given view should show the tag filter bar */
export function viewShowsTagFilter(viewId: ViewId): boolean {
  return TAG_FILTER_VIEWS.has(viewId)
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useTagFilterStore = create<TagFilterState>()((set, get) => ({
  selectedTags: [],
  availableTags: [],
  filterText: "",

  setSelectedTags: (tags) => set({ selectedTags: tags }),

  setAvailableTags: (tags) => set({ availableTags: tags }),

  setFilterText: (text) => set({ filterText: text }),

  toggleTag: (tag) => {
    const { selectedTags } = get()

    if (selectedTags.length === 0) {
      // "All" was active → select only this tag
      set({ selectedTags: [tag] })
      return
    }

    if (selectedTags.includes(tag)) {
      // Deselect this tag (if last one, reverts to "All")
      set({ selectedTags: selectedTags.filter((t) => t !== tag) })
    } else {
      // Add tag to selection
      set({ selectedTags: [...selectedTags, tag] })
    }
  },

  selectAll: () => set({ selectedTags: [] }),

  reset: () => set({ selectedTags: [], availableTags: [], filterText: "" }),

  isFiltering: () => {
    const { selectedTags, filterText } = get()
    return selectedTags.length > 0 || filterText.trim().length > 0
  },
}))

// ---------------------------------------------------------------------------
// Derived helpers
// ---------------------------------------------------------------------------

/**
 * Filter items by selected tags.
 * When `selectedTags` is empty, returns all items (no filter).
 * Otherwise returns only items where the task has ALL selected tags.
 *
 * Expects enriched items with `allTags` (own + inherited ancestor tags).
 */
export function filterBySelectedTags<T extends { allTags: string[] }>(
  items: T[],
  selectedTags: string[],
): T[] {
  if (selectedTags.length === 0) return items
  return items.filter((item) =>
    selectedTags.every((st) => item.allTags.includes(st)),
  )
}

/**
 * Filter items by free-text search against task title.
 * Case-insensitive substring match. Empty text returns all items.
 */
export function filterByText<T extends { task: { title: string } }>(
  items: T[],
  text: string,
): T[] {
  const trimmed = text.trim().toLowerCase()
  if (trimmed.length === 0) return items
  return items.filter((item) => item.task.title.toLowerCase().includes(trimmed))
}

/**
 * Extract unique sorted tags from a list of items.
 * Used to populate `availableTags` for the current view.
 *
 * Expects enriched items with `allTags` (own + inherited ancestor tags).
 */
export function getUniqueTagsFromItems<T extends { allTags: string[] }>(
  items: T[],
): string[] {
  const tagSet = new Set<string>()
  for (const item of items) {
    if (item.allTags) {
      for (const tag of item.allTags) {
        tagSet.add(tag)
      }
    }
  }
  return Array.from(tagSet).sort((a, b) => a.localeCompare(b))
}
